import { ClientResponse } from '@orbis-systems/orbis-api-clients/orbis';
import { find, flatMap, groupBy, isArray, isFinite } from 'lodash';
import numbro from 'numbro';
import { inject, injectable } from 'tsyringe';
import { CachedResponseApi } from '../BaseApi';
import { ICache } from '../Cache';
import { ModelFractionalSharePolicy, OrderFilterType, OrderTransaction, OverExposedType } from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import {
    Account,
    Apis,
    HoldingInfo,
    MiddlewareConfig,
    Model,
    ModelPortfolioOptions,
    ModelWithExtraInfo,
    Order,
    OrderWithExtraInfo,
    OverExposedAccount,
} from '../types';
import { ModelDriftReportItem, RebalanceRecommendationItem } from '../types/DriftReport.type';
import { Portfolio, PortfolioWithExtraInfo, PositionWithExtraInfo } from '../types/Portfolio.type';
import {
    formatDriftReport,
    formatHoldingInfo,
    formatOrders,
    formatOverExposedAccounts,
    formatPortfolio,
    formatQuote,
} from '../utils/dataUtils';
import { divide, multiply, subtract, sumBy } from '../utils/numberUtils';
import type { BranchApi } from './BranchApi';
import type { UserApi } from './UserApi';

@injectable()
export class ModelApi extends CachedResponseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
        @inject(ApiInjectToken.BranchApi) private readonly branchApi: BranchApi,
        @inject(ApiInjectToken.UserApi) protected readonly userApi: UserApi,
    ) {
        super(apis, config, cache);
    }

    async getModels(): Promise<ModelWithExtraInfo[]> {
        const data = await Promise.all([
            this.apis.orbis.advisory.models.memberRealtimeBalances('models'),
            this.apis.orbis.advisory.models.listing({ loadQuotes: true, loadAccounts: true }),
            this.branchApi.getAccounts(),
        ]);

        const models = data[1].data as Model[];

        return models.map((model) => {
            const modelRtbsData = find(data[0].data, (item) => item.modelId === model.id) || {};

            const {
                balance = 0,
                cashOnAccount = 0,
                markToMarket = 0,
                sodCashBalance = 0,
                sodMarginBalance = 0,
                sodShortBalance = 0,
                totalDividendAmount = 0,
                totalDividendYield = 0,
            } = modelRtbsData;

            const accountIds = model.accounts.map((account) => account.accountId);
            const accounts = data[2].filter((account) => accountIds.includes(account.accountId));

            model.components.forEach((component) => {
                component.quote = formatQuote(component.quote);
            });

            return {
                ...model,
                balance,
                cashOnAccount,
                markToMarket,
                sodCashBalance,
                sodMarginBalance,
                sodShortBalance,
                totalDividendAmount,
                totalDividendYield,
                accounts,
            };
        });
    }

    async getModel(modelId: number): Promise<ModelWithExtraInfo | null> {
        const data = await this.getModels();
        return data.find((model) => model.id === modelId) || null;
    }

    async getPortfolio(modelId: number, options?: ModelPortfolioOptions): Promise<PortfolioWithExtraInfo> {
        const { combined, symbol, currency, positionType } = options || {};
        const data = await Promise.all([
            this.branchApi.getAccounts(),
            this.getModels(),
            this.apis.orbis.advisory.models.portfolio(modelId !== 0 ? `${modelId}` : 'All', {
                // @ts-expect-error: // FIXME: missing query param (currency). Waiting for backend update
                currency,
                loadQuotes: true,
                loadIndustries: true,
                loadCost: !combined,
                combined,
            }),
        ]);

        const accounts = data[0];
        const model = data[1].find((model) => model.id === modelId);
        const portfolio = data[2].data as Portfolio;

        return formatPortfolio(portfolio, accounts, [], model?.balance || 0, { symbol, positionType: positionType });
    }

    async getOrders(
        modelId: number,
        filterType: OrderFilterType | OrderFilterType[] = OrderFilterType.Latest,
    ): Promise<OrderWithExtraInfo[]> {
        const data = await Promise.all([
            this.apis.orbis.advisory.models.listing(),
            this.userApi.getUserInfo(),
            this.branchApi.getInventoryAccounts(),
            ...(isArray(filterType) ? filterType : [filterType]).map((filter) => {
                return this.apis.orbis.orders.listingForModel(`${modelId}`, filter, { loadQuotes: true });
            }),
        ]);

        const models = data[0].data as Model[];
        const accounts = data[1].accountList;
        const inventoryAccounts = data[2] as Account[];
        const orders = flatMap(data.slice(3) as ClientResponse[], (item) => item.data) as Order[];

        return formatOrders({
            orders,
            models,
            accounts,
            inventoryAccount: inventoryAccounts[0],
        });
    }

    async getOverExposedAccounts(
        modelId: number,
        threshold: number,
        type: OverExposedType,
    ): Promise<OverExposedAccount[]> {
        const { data } = await this.apis.orbis.advisory.analytics.overexposureAnalysis(
            `${type}`,
            'model',
            `${modelId}`,
            { threshold },
        );
        const accounts = await this.branchApi.getAccounts();

        return formatOverExposedAccounts(data, type, accounts);
    }

    async getDriftReport(modelId: number, accountIds: number[] = []): Promise<ModelDriftReportItem[]> {
        const data = await Promise.all([this.getModel(modelId), this.getPortfolio(modelId, { combined: false })]);

        const model = data[0];
        const positions = data[1].entries;
        let combinedPositions: PositionWithExtraInfo[] = [];

        if (accountIds.length) {
            const accountPositions = positions.filter(
                (position) => accountIds.includes(position.accountId) && !position.short && !position.option,
            );

            if (accountPositions.length) {
                const positionsGroupedBySymbol = groupBy(accountPositions, (position) => position.symbol);
                combinedPositions = Object.values(positionsGroupedBySymbol).map((positions) =>
                    positions.reduce<PositionWithExtraInfo>((result, position, index) => {
                        if (index !== 0) {
                            result.value += position.value;
                            result.quantity += position.quantity;
                        }
                        return result;
                    }, positions[0]!),
                );
            }
        } else {
            combinedPositions = (await this.getPortfolio(modelId, { combined: true })).entries.filter(
                (position) => !position.short && !position.option,
            );
        }

        if (!model) {
            return [];
        }

        return formatDriftReport(model, positions, combinedPositions);
    }

    async getHoldingInfo(modelId: number, symbol: string, currency?: string): Promise<HoldingInfo> {
        const data = await Promise.all([
            this.getModel(modelId),
            this.getPortfolio(modelId, { combined: true, currency }),
        ]);

        const model = data[0];
        const positions = data[1].entries;
        const modelComponent = model?.components.filter((component) => component.symbol === symbol)[0];

        return formatHoldingInfo(symbol, positions, model?.balance || 0, modelComponent?.percentage || 0);
    }

    /*
        Model rebalancing steps:
          - Get model buying power
          - Get model's account buying powers
          - Get model portfolio (uncombined, no short positions)
          - For positions that have target = 0: sell all
          - For positions that have actual > target: sell the difference proportionally
          - For positions that have actual < target: buy the difference proportionally
    */
    async getRebalanceRecommendations(
        modelId: number,
        targets: Record<string, number>,
        accountIds: number[] = [],
        presets: {
            accountId: number;
            transaction: OrderTransaction.Buy | OrderTransaction.Sell;
            symbol: string;
            quantity: number;
        }[] = [],
        fractionalSharePolicy = ModelFractionalSharePolicy.RoundDown,
        fractionalSharePrecision = 5,
    ): Promise<RebalanceRecommendationItem[]> {
        const model = await this.getModel(modelId);

        if (!model) {
            return [];
        }

        const results: RebalanceRecommendationItem[] = [];
        const [driftReportItems, modelPortfolio] = await Promise.all([
            this.getDriftReport(modelId),
            this.getPortfolio(modelId, { combined: false }),
        ]);

        const buyingPowers = (
            await Promise.all(
                model.accounts.map((account) =>
                    this.apis.orbis.user.accountTradingLimit({ accountId: account.accountId }),
                ),
            )
        ).map((item) => item.data.availableCash as number);

        const accounts = model.accounts
            .map<RebalanceRecommendationItem['account']>((account, index) => ({
                ...account,
                buyingPower: buyingPowers[index] || 0,
                expectedBuyingPower: buyingPowers[index] || 0,
            }))
            .reduce<Record<string, RebalanceRecommendationItem['account']>>((result, account) => {
                result[account.accountId] = account;
                return result;
            }, {});

        const positions = groupBy(
            modelPortfolio.entries.filter((position) => !position.short && !position.option),
            (item) => item.symbol,
        );

        // Calculations
        Object.keys(targets).forEach((symbol) => {
            const current = driftReportItems.find((item) => item.symbol === symbol);

            if (!current) {
                return;
            }

            const quote = current.quote;
            const expectedPrice = quote.mutualFund ? quote.navPrice : quote.lastPrice || 0;
            const target = targets[symbol];

            if (target === 0) {
                positions[symbol]?.forEach((position) => {
                    const account = accounts[position.accountId]!;
                    const positionWeight = divide(position.value || 0, account.balance);
                    const sellValue = multiply(expectedPrice, position.quantity);

                    results.push({
                        account,
                        transaction: OrderTransaction.Sell,
                        quantity: position.quantity,
                        currentWeight: positionWeight,
                        targetWeight: target,
                        error: '',
                        expectedPrice: expectedPrice,
                        expectedValue: sellValue,
                        position,
                        symbol,
                        quote,
                    });
                });
            } else if (target) {
                Object.values(accounts).forEach((account) => {
                    const position = positions[symbol]?.find((position) => position.accountId === account.accountId);
                    const positionWeight = divide(position?.value || 0, account.balance);

                    if (positionWeight === target || (accountIds.length && !accountIds.includes(account.accountId))) {
                        return;
                    }

                    const transaction = positionWeight > target ? OrderTransaction.Sell : OrderTransaction.Buy;
                    const maxQuantity =
                        transaction === OrderTransaction.Sell
                            ? position?.quantity || 0
                            : Math.max(0, divide(account.buyingPower, expectedPrice));
                    let expectedValue = multiply(account.balance, Math.abs(subtract(positionWeight, target)));
                    let expectedQuantity = +numbro(Math.min(maxQuantity, divide(expectedValue, expectedPrice))).format({
                        mantissa: fractionalSharePrecision,
                        roundingFunction: Math.floor,
                    });

                    if (fractionalSharePolicy !== ModelFractionalSharePolicy.Fractional) {
                        expectedQuantity =
                            fractionalSharePolicy === ModelFractionalSharePolicy.RoundDown
                                ? Math.floor(expectedQuantity)
                                : Math.ceil(expectedQuantity);
                        expectedValue = multiply(expectedQuantity, expectedPrice);
                    }

                    results.push({
                        account,
                        transaction,
                        quantity: expectedQuantity,
                        currentWeight: positionWeight,
                        targetWeight: target,
                        error: '',
                        expectedPrice,
                        expectedValue,
                        position,
                        symbol,
                        quote,
                    });
                });
            }
        });

        // Prevent NaN or Infinity
        results.forEach((item) => {
            item.quantity = isFinite(item.quantity) ? item.quantity : 0;
            item.expectedValue = isFinite(item.expectedValue) ? item.expectedValue : 0;
        });

        const resultsGroupedByAccount = groupBy(results, (item) => item.account.accountId);

        // Checking buying transactions to prevent buying more than buying power
        Object.keys(resultsGroupedByAccount).forEach((accountId) => {
            const account = accounts[accountId]!;
            const buyingPower = account.buyingPower;
            const positions = resultsGroupedByAccount[accountId]!;
            const buyingPositions = positions.filter((item) => item.transaction === OrderTransaction.Buy);
            const sellingPositions = positions.filter((item) => item.transaction === OrderTransaction.Sell);
            const totalBuyingValue = sumBy(buyingPositions, (item) => item.expectedValue);
            const totalSellingValue = sumBy(sellingPositions, (item) => item.expectedValue);

            account.expectedBuyingPower += totalSellingValue - totalBuyingValue;

            if (buyingPower < 0 || totalBuyingValue <= buyingPower) {
                return;
            }

            const buyingValueReduceRatio = divide(buyingPower, totalBuyingValue);
            positions
                .filter((item) => item.transaction === OrderTransaction.Buy)
                .forEach((item) => {
                    const previousExpectedValue = item.expectedValue;
                    item.expectedValue = multiply(item.expectedValue, buyingValueReduceRatio);
                    item.quantity = +numbro(divide(item.expectedValue, item.expectedPrice)).format({
                        mantissa: fractionalSharePrecision,
                        roundingFunction: Math.floor,
                    });

                    if (fractionalSharePolicy !== ModelFractionalSharePolicy.Fractional) {
                        item.quantity =
                            fractionalSharePolicy === ModelFractionalSharePolicy.RoundDown
                                ? Math.floor(item.quantity)
                                : Math.ceil(item.quantity);
                        item.expectedValue = multiply(item.quantity, item.expectedPrice);
                    }

                    // need to add back the amount that we couldn't buy
                    account.expectedBuyingPower += subtract(previousExpectedValue, item.expectedValue);
                });
        });

        presets.forEach((preset) => {
            const recommendation = results.find(
                (item) => item.account.accountId === preset.accountId && item.symbol === preset.symbol,
            );
            if (recommendation) {
                recommendation.quantity = preset.quantity;
                recommendation.expectedValue = multiply(preset.quantity, recommendation.expectedPrice);
                recommendation.transaction = preset.transaction;
            }
        });

        return results;
    }
}
